import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { toast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { useAnswerAlert, useMyAlerts } from './hooks';
import { LevelPill, errorInfo } from './ui';
import type { DoctorAlert, DoctorAlertStatus } from './types';

/**
 * The ED page popup (backend/utils/erDoctorAlerts.js). Mounted once in
 * App.tsx, so it appears on whatever screen the doctor is on — including the
 * middle of an OPD consultation.
 *
 * A small card in the corner, not a modal: the doctor may be mid-sentence
 * with a patient and must be able to finish the sentence. It beeps once per
 * new page and flashes the tab title, so a doctor on another tab still sees it.
 *
 * Accept → the server holds the current OPD consultation (if any) and this
 * opens the ED patient. Can't come → back to the ED board for reassignment.
 */
export default function DoctorAlertPopup({ onOpenEncounter }: { onOpenEncounter: (encounterId: number) => void }) {
  const { user } = useAuth();
  const modules = user?.modules ?? [];
  const canAct = modules.includes('emergency') && modules.includes('er:clinical');
  const q = useMyAlerts(canAct);
  const answer = useAnswerAlert();
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const now = useNow(1000, !!q.data?.alerts.length);

  const alerts = q.data?.alerts ?? [];
  const alert = alerts[0];
  useBeepOnNew(alerts);
  useFlashingTitle(alerts.length > 0);

  // A different page on top resets the decline form.
  const topId = alert?.id;
  useEffect(() => { setDeclining(false); setReason(''); }, [topId]);

  if (!canAct || !alert) return null;
  const active = q.data?.active_consultation ?? null;
  const secondsLeft = Math.round((new Date(alert.expires_at).getTime() - now) / 1000);
  const pagedMin = Math.max(0, Math.floor((now - new Date(alert.paged_at).getTime()) / 60000));

  const respond = async (accept: boolean) => {
    try {
      const r = await answer.mutateAsync({ alertId: alert.id, encounterId: alert.encounter_id, accept, reason: reason || undefined });
      if (accept) {
        const held = r.held_consultation;
        toast(held
          ? { title: `${held.patient_name}'s consultation is on hold`, description: 'Resume it from the Queue when you are back.', variant: 'warn' }
          : { title: 'Accepted — opening the ED patient', variant: 'success' });
        onOpenEncounter(alert.encounter_id);
      } else {
        toast({ title: 'Sent to the ED', description: `"${reason}" — they will assign another doctor.`, variant: 'success' });
      }
    } catch (e) {
      toast({ title: accept ? 'Could not accept' : 'Could not decline', description: errorInfo(e).message, variant: 'danger' });
    }
  };

  return (
    <div role="alertdialog" aria-live="assertive" aria-label="Emergency page"
      className="fixed bottom-4 right-4 z-[250] w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border-2 border-danger bg-card shadow-2xl">
      <div className="flex items-center justify-between gap-2 bg-danger px-4 py-2 text-white">
        <div className="flex items-center gap-2 text-[13px] font-bold">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" />
          </span>
          EMERGENCY{alert.bay_code ? ` · Bay ${alert.bay_code}` : ''}
        </div>
        {alerts.length > 1 && <span className="text-[11px] font-semibold">+{alerts.length - 1} more</span>}
      </div>

      <div className="space-y-2 p-4 text-[13px]">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-ink">{alert.patient_name}</span>
          <span className="text-[12px] text-ink-muted">{patientLine(alert)}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <LevelPill color={alert.level_color} label={alert.current_triage_level ? (alert.level_label ?? alert.current_triage_level) : null} />
          {alert.is_mlc && <Badge variant="warn">MLC</Badge>}
          <span className="text-[12px] text-ink-muted">{alert.er_number}</span>
        </div>
        {alert.chief_complaint && <div className="text-ink">{alert.chief_complaint}</div>}
        <div className="text-[12px] text-ink-muted">
          Assigned by {alert.paged_by ?? 'ED'} · {pagedMin === 0 ? 'just now' : `${pagedMin} min ago`}
          {' · '}
          {secondsLeft > 0
            ? <span>respond within <span className="font-mono font-semibold text-danger-ink">{mmss(secondsLeft)}</span></span>
            : <span className="font-semibold text-danger-ink">no answer recorded — you can still accept</span>}
        </div>

        {active && (
          <div className="rounded-md border border-warn bg-warn-soft px-3 py-2 text-[12px] text-warn-ink">
            You are consulting <b>{active.patient_name}</b>{active.display_token ? ` (Token ${active.display_token})` : ''}.
            Accepting puts that consultation <b>on hold</b> — resume it from the Queue later.
          </div>
        )}

        {declining ? (
          <div className="space-y-2 pt-1">
            <div className="text-[12px] font-semibold text-ink">Why can't you come? The ED sees this and calls another doctor.</div>
            <div className="flex flex-wrap gap-1.5">
              {DECLINE_REASONS.map((r) => (
                <button key={r} type="button" onClick={() => setReason(r)}
                  className={reason === r
                    ? 'rounded-full border border-danger bg-danger-soft px-2.5 py-1 text-[12px] font-semibold text-danger-ink'
                    : 'rounded-full border border-border px-2.5 py-1 text-[12px] text-ink hover:bg-muted'}>
                  {r}
                </button>
              ))}
            </div>
            <Input value={DECLINE_REASONS.includes(reason) ? '' : reason} maxLength={300} placeholder="Other reason…"
              onChange={(e) => setReason(e.target.value)} className="h-8 text-[12px]" />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setDeclining(false)} disabled={answer.isPending}>Back</Button>
              <Button size="sm" variant="primary" loading={answer.isPending} disabled={!reason.trim()} onClick={() => respond(false)}>
                Send — I can't come
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="ghost" onClick={() => setDeclining(true)} disabled={answer.isPending}>Can't come</Button>
            <Button size="sm" variant="danger" loading={answer.isPending} onClick={() => respond(true)}>
              {active ? 'Hold OPD & go to ED' : 'Accept & go to ED'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The attending's page status, for the board and the workspace header. Shows
 * exactly what the server says — never "No answer" from the browser's own
 * clock, because a screen that missed a refresh would then contradict a
 * decline the doctor already sent. The server settles expiry on every read.
 */
export function DoctorPageStatus({ status, pagedAt, declineReason, className }: {
  status: DoctorAlertStatus | null | undefined; pagedAt?: string | null;
  declineReason?: string | null; className?: string;
}) {
  if (!status || status === 'cancelled') return null;
  const s: DoctorAlertStatus = status;
  const since = pagedAt ? Math.max(0, Math.floor((Date.now() - new Date(pagedAt).getTime()) / 60000)) : null;
  const map: Record<Exclude<DoctorAlertStatus, 'cancelled'>, { label: string; variant: 'warn' | 'success' | 'danger' }> = {
    pending: { label: `📟 Paging${since !== null ? ` · ${since}m` : ''}`, variant: 'warn' },
    accepted: { label: '✓ Doctor coming', variant: 'success' },
    declined: { label: '✕ Doctor declined', variant: 'danger' },
    expired: { label: '⏱ No answer', variant: 'danger' },
  };
  const m = map[s as Exclude<DoctorAlertStatus, 'cancelled'>];
  return <Badge variant={m.variant} className={className} title={s === 'declined' && declineReason ? declineReason : undefined}>{m.label}</Badge>;
}

/**
 * The ED side of a decline or an unanswered page: a red banner on the
 * encounter saying which doctor cannot come and why, with the two ways
 * forward — choose another doctor, or page the same one again.
 */
export function DoctorCantComeBanner({ alert, doctorName, onChooseAnother, onPageAgain, paging, canAct }: {
  alert: DoctorAlert; doctorName: string | null; onChooseAnother: () => void; onPageAgain: () => void;
  paging: boolean; canAct: boolean;
}) {
  if (alert.status !== 'declined' && alert.status !== 'expired') return null;
  const declined = alert.status === 'declined';
  const at = alert.responded_at ?? alert.expires_at;
  return (
    <div role="alert" className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-danger/40 bg-danger-soft px-4 py-3 text-danger-ink">
      <div className="min-w-0 flex-1 text-[13px]">
        <div className="font-semibold">
          {declined ? `✕ ${drName(doctorName)} can't come` : `⏱ ${drName(doctorName)} has not answered`}
          {declined && alert.decline_reason ? <> — <span className="font-bold">{alert.decline_reason}</span></> : null}
        </div>
        <div className="text-[12px]">
          {declined ? 'Declined' : 'Page timed out'} at {new Date(at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}.
          {' '}Choose another doctor{declined ? '' : ', or page again'}.
        </div>
      </div>
      {canAct && (
        <div className="flex gap-2">
          <Button size="sm" variant="danger" onClick={onChooseAnother}>👨‍⚕️ Choose another doctor</Button>
          <Button size="sm" variant="ghost" loading={paging} onClick={onPageAgain}>📟 Page {drName(doctorName)} again</Button>
        </div>
      )}
    </div>
  );
}

/** "Dr. Sekar" — without doubling a "Dr." that is already part of the user's name. */
export function drName(name: string | null | undefined): string {
  const n = (name ?? '').trim();
  if (!n) return 'The doctor';
  return /^dr(\.|\s)/i.test(n) ? n.replace(/^dr\.?\s*/i, 'Dr. ') : `Dr. ${n}`;
}

// The common answers, one tap each. Anything else goes in "Other reason".
const DECLINE_REASONS = [
  'In operation theatre',
  'Attending another emergency',
  'On ward round',
  'In a procedure',
  'Not in the hospital',
];

function patientLine(a: DoctorAlert) {
  const age = a.age != null ? `${a.age}y` : a.approx_age_years != null ? `~${a.approx_age_years}y` : '';
  return [age, a.gender].filter(Boolean).join(' ');
}

function mmss(sec: number) {
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
}

function useNow(ms: number, running: boolean) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return;
    setNow(Date.now());
    const t = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(t);
  }, [ms, running]);
  return now;
}

/** One short two-tone beep per page id not seen before. Silently does nothing
 * where the browser blocks audio until the user has interacted with the page. */
function useBeepOnNew(alerts: DoctorAlert[]) {
  const seen = useRef<Set<number>>(new Set());
  useEffect(() => {
    const fresh = alerts.filter((a) => !seen.current.has(a.id));
    alerts.forEach((a) => seen.current.add(a.id));
    if (!fresh.length) return;
    try {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      [0, 0.25, 0.5].forEach((t, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.frequency.value = i % 2 ? 660 : 880;
        g.gain.setValueAtTime(0.15, ctx.currentTime + t);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + 0.2);
        o.connect(g); g.connect(ctx.destination);
        o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + 0.2);
      });
      window.setTimeout(() => { ctx.close().catch(() => {}); }, 1200);
    } catch { /* audio blocked — the card and the title still show */ }
  }, [alerts]);
}

function useFlashingTitle(on: boolean) {
  useEffect(() => {
    if (!on) return;
    const original = document.title;
    let flip = false;
    const t = window.setInterval(() => { flip = !flip; document.title = flip ? '🚨 ED PAGE' : original; }, 1000);
    return () => { window.clearInterval(t); document.title = original; };
  }, [on]);
}
